import React, { useEffect } from 'react';
import { faChevronUp, faHotel, faLanguage, faPlane } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FC, Fragment, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getLogoUrl, formatPrice } from 'utils/utils';
import { faFileLines, faMoneyBill1 } from '@fortawesome/free-regular-svg-icons';
import { AdditionalPayment, BookingModel } from 'ts/package/interfaces/BookPackageResponse';
import { formatOccupancySrt, getDealType, getFare, getFlight } from 'utils/package';
import Rating from 'components/ui/Rating/Rating';
import { useLocation, useSearchParams } from 'react-router-dom';
import HandleFee from 'components/shared/HandleFee';
import SalesPrice from 'components/shared/SalesPrice';
import { dateFormat } from 'utils/shared/utils';
import withErrorBoundary from 'components/shared/withErrorBoundary';
import { CmsPageResponse } from 'ts/shared/interfaces/CmsPageResponse';
import useThrottle from 'hooks/useThrottle';
import GeneralConditionsModal from 'components/shared/GeneralConditionsModal';
import { PassengerType } from 'constants/package';

import TicketBaggagePac from './TicketBaggagePac';
import ReactCountryFlag from 'react-country-flag';
import { GetPackageDetailsRequest } from 'ts/package/classes/GetPackageDetailsRequest';
import MoreDetailsModal from './MoreDetailsModal';
import { blobUrl } from 'constants/generic';

interface BookSummaryProps {
  successPage?: boolean;
  bookingModel: BookingModel;
  totalPrice: number;
  couponAmount: string;
  setCouponAmount: (value: string) => void;
  onClickApply: () => void;
  netravelPage?: CmsPageResponse;
  additionalPaymentsSelected: AdditionalPayment[];
  packageDetailsParams?: GetPackageDetailsRequest;
}

const BookSummary: FC<BookSummaryProps> = ({
  bookingModel: { details, rooms, fareBasisBoardDesc, currency, paxes },
  successPage,
  totalPrice,
  ...props
}) => {
  
  const { t, i18n } = useTranslation();
  const { pathname } = useLocation();
  const [isTermsOpen, setIsTermsOpen] = useState(false);
  const isTour = useRef(pathname.includes('tour')).current;
  const { infantPrice } = details.deal;
  const [urlParams] = useSearchParams();
  const flight = getFlight(details.options, +(urlParams.get('flightOptIndex') || '0'));
  const flightDeal = details.flights?.[+(urlParams.get('flightIndex') || '0')];
  const { cityFullDescription, itineraries } = details.additionalInfo;
  const infantsCount = paxes.filter((pax) => pax.paxTypeCode === PassengerType.Infant).length;
  const [isLockClass, setIsLockClass] = useState(false);
  const totalRef = useRef<HTMLDivElement>(null);
  const [isMoreDetailsOpen, setIsMoreDetailsOpen] = useState(false);

  useEffect(() => {
    if (window.innerWidth > 768) return;
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleScroll = useThrottle(() => {
    if (totalRef.current) {
      const totalRefTop = totalRef.current.getBoundingClientRect().top;

      if (totalRefTop < 0) {
        setIsLockClass(true);
      } else {
        setIsLockClass(false);
      }
    }
  }, 300);

  const getPaymentDesc = (payment: AdditionalPayment) => {
    if (i18n.language === 'he') {
      return payment.descLocal || payment.value;
    }
    return payment.value || payment.descLocal;
  };

  return (
    <div>
      <div
        className={
          'details-summary book-summary default-shadow flight-details-summary package-summary position-relative ' +
          (successPage ? ' flight-details-summary-success' : '')
        }
      >
        <div className="title-underline text-lg title-header-grey">
          {t('passenger.order_details')}
        </div>
        {!!details.departureAirport && !!details.arrivalAirport && (
          <Fragment>
            <div className="title text-st text-contrast">
              <FontAwesomeIcon icon={faPlane} className="mr-5" size="sm" />
              {t('flight.details')}
            </div>

            <div className="summary">
              {!!flight && (
                <span className="summary-img">
                  <img
                    src={getLogoUrl(blobUrl, flight.outbound.airline)}
                    alt={flight.outbound.airline}
                    onError={(e) => (e.target as HTMLImageElement).parentElement?.remove()}
                  />
                  <span className="text-light">{flight.outbound.airline}</span>
                </span>
              )}
              {!!flightDeal && (
                <span className="summary-img">
                  <img
                    src={getLogoUrl(blobUrl, flightDeal.outbound?.[0].airline)}
                    alt={flightDeal.outbound?.[0].airline}
                    onError={(e) => (e.target as HTMLImageElement).parentElement?.remove()}
                  />
                  <span className="text-light">{flightDeal.outbound?.[0].airline}</span>
                </span>
              )}

              <div className="summary-road">
                <div className="summary-road-item">
                  <div>
                    <ReactCountryFlag
                      className="mr-5"
                      svg
                      countryCode={details.departureAirport.countryCode}
                    />
                    <span>
                      {details.departureAirport.airCityLang.desc} ({details.departureAirport.code})
                    </span>
                    <br />
                    <span className="text-light text-xxs">{details.departureAirport.desc}</span>
                  </div>

                  {!!flight && (
                    <div>
                      <span>
                        {dateFormat(
                          `${flight?.outbound.date}T${flight?.outbound.depTime}`,
                          'EEE - dd MMMM, yyyy'
                        )}{' '}
                      </span>
                      <span className="text-bold">
                        {dateFormat(
                          `${flight?.outbound.date}T${flight?.outbound.depTime}`,
                          'HH:mm'
                        )}
                      </span>
                    </div>
                  )}

                  {!!flightDeal && (
                    <div>
                      <span>
                        {dateFormat(
                          `${flightDeal.outbound?.[0].date}T${flightDeal.outbound?.[0].depTime}`,
                          'EEE - dd MMMM, yyyy'
                        )}{' '}
                      </span>
                      <span className="text-bold">
                        {dateFormat(
                          `${flightDeal.outbound?.[0].date}T${flightDeal.outbound?.[0].depTime}`,
                          'HH:mm'
                        )}
                      </span>
                    </div>
                  )}
                </div>
                <div className="summary-road-item">
                  <div>
                    <ReactCountryFlag
                      className="mr-5"
                      svg
                      countryCode={details.arrivalAirport.countryCode}
                    />
                    <span>
                      {details.arrivalAirport.airCityLang.desc} ({details.arrivalAirport.code})
                    </span>
                    <br />
                    <span className="text-light text-xxs">{details.arrivalAirport.desc}</span>
                  </div>
                  {!!flight && (
                    <div>
                      <span>
                        {dateFormat(
                          `${flight?.outbound.date}T${flight?.outbound.arrTime}`,
                          'EEE - dd MMMM, yyyy'
                        )}{' '}
                      </span>
                      <span className="text-bold">
                        {dateFormat(
                          `${flight?.outbound.date}T${flight?.outbound.arrTime}`,
                          'HH:mm'
                        )}
                      </span>
                    </div>
                  )}
                  {!!flightDeal && (
                    <div>
                      <span>
                        {dateFormat(
                          `${flightDeal.outbound?.[0].date}T${flightDeal.outbound?.[0].arrTime}`,
                          'EEE - dd MMMM, yyyy'
                        )}{' '}
                      </span>
                      <span className="text-bold">
                        {dateFormat(
                          `${flightDeal.outbound?.[0].date}T${flightDeal.outbound?.[0].arrTime}`,
                          'HH:mm'
                        )}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="summary">
              {!!flight && (
                <span className="summary-img">
                  <img
                    src={getLogoUrl(blobUrl, flight?.inbound.airline)}
                    alt={flight?.inbound.airline}
                    onError={(e) => (e.target as HTMLImageElement).parentElement?.remove()}
                  />
                  <span className="text-light">{flight?.inbound.airline}</span>
                </span>
              )}
              {!!flightDeal && (
                <span className="summary-img">
                  <img
                    src={getLogoUrl(blobUrl, flightDeal.inbound?.[0].airline)}
                    alt={flightDeal.inbound?.[0].airline}
                    onError={(e) => (e.target as HTMLImageElement).parentElement?.remove()}
                  />
                  <span className="text-light">{flightDeal.inbound?.[0].airline}</span>
                </span>
              )}

              <div className="summary-road">
                <div className="summary-road-item">
                  <div>
                    <ReactCountryFlag
                      className="mr-5"
                      svg
                      countryCode={details.arrivalAirport.countryCode}
                    />
                    <span>
                      {details.arrivalAirport.airCityLang.desc} ({details.arrivalAirport.code})
                    </span>
                    <br />
                    <span className="text-light text-xxs">{details.arrivalAirport.desc}</span>
                  </div>
                  {!!flight && (
                    <div>
                      <span>
                        {dateFormat(
                          `${flight?.inbound.date}T${flight?.inbound.depTime}`,
                          'EEE - dd MMMM, yyyy'
                        )}{' '}
                      </span>
                      <span className="text-bold">
                        {dateFormat(`${flight?.inbound.date}T${flight?.inbound.depTime}`, 'HH:mm')}
                      </span>
                    </div>
                  )}
                  {!!flightDeal && (
                    <div>
                      <span>
                        {dateFormat(
                          `${flightDeal.inbound?.[0].date}T${flightDeal.inbound?.[0].depTime}`,
                          'EEE - dd MMMM, yyyy'
                        )}{' '}
                      </span>
                      <span className="text-bold">
                        {dateFormat(
                          `${flightDeal.inbound?.[0].date}T${flightDeal.inbound?.[0].depTime}`,
                          'HH:mm'
                        )}
                      </span>
                    </div>
                  )}
                </div>
                <div className="summary-road-item">
                  <div>
                    <ReactCountryFlag
                      className="mr-5"
                      svg
                      countryCode={details.departureAirport.countryCode}
                    />
                    <span>
                      {details.departureAirport.airCityLang.desc} ({details.departureAirport.code})
                    </span>
                    <br />
                    <span className="text-light text-xxs">{details.departureAirport.desc}</span>
                  </div>
                  {!!flight && (
                    <div>
                      <span>
                        {dateFormat(
                          `${flight?.inbound.date}T${flight?.inbound.arrTime}`,
                          'EEE - dd MMMM, yyyy'
                        )}{' '}
                      </span>
                      <span className="text-bold">
                        {dateFormat(`${flight?.inbound.date}T${flight?.inbound.arrTime}`, 'HH:mm')}
                      </span>
                    </div>
                  )}
                  {!!flightDeal && (
                    <div>
                      <span>
                        {dateFormat(
                          `${flightDeal.inbound?.[0].date}T${flightDeal.inbound?.[0].arrTime}`,
                          'EEE - dd MMMM, yyyy'
                        )}{' '}
                      </span>
                      <span className="text-bold">
                        {dateFormat(
                          `${flightDeal.inbound?.[0].date}T${flightDeal.inbound?.[0].arrTime}`,
                          'HH:mm'
                        )}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="text-lg mb-10 flex-row">
              <TicketBaggagePac dealBaggage={details.deal?.dealBaggage} shortMode={true} />
            </div>
            <div className="divider mb-20"></div>
          </Fragment>
        )}
        <div className="summary-road">
          <div className="summary-road-title">
            <div className="title text-st text-contrast mb-0 text-lh">
              <FontAwesomeIcon icon={isTour ? faLanguage : faHotel} className="mr-5" size="sm" />
              <span className="text-cap">
                {t(getDealType(details.deal.dealType, 1)?.toLowerCase())}
                {details.deal.dealTitle?.toLowerCase()}
              </span>
            </div>
            {!isTour && (
              <div className="text-lh">
                <div className="text-cap text-light text-normal">
                  {details.info.hotelLocation?.cityName?.toLowerCase()}
                </div>
                <span className="text-sm">{t('hotel.hotel')} </span>
                <span className="text-bold text-cap text-sm">
                  {details.info.hotelName?.toLowerCase()}
                </span>
                <br />
                <Rating className="mb-20" value={details.info.hotelRate} />
              </div>
            )}
          </div>
          <div className="summary-road-image">
            <img
              src={details.info.hotelImages?.length ? details.info.hotelImages[0].url : ''}
              alt={details.info.hotelName}
            />
          </div>
        </div>
        <div className="summary-road text-sm text-lh">
          <div className="summary-road-item">
            {isTour ? (
              <Fragment>
                <div className="text-sm">{t('tour.tour')}</div>
                <div className="text-bold mb-10">{details.deal.rowName}</div>
              </Fragment>
            ) : (
              <Fragment>
                <div className="text-sm">{t('search.check_in')}</div>
                <div className="text-bold mb-10">
                  {dateFormat(details.deal.departureDate, 'EEE - dd MMMM, yyyy')}
                </div>
              </Fragment>
            )}
            <div>{t('package.nights')}</div>
            <div className="text-bold mb-10">{details.deal.dealLength}</div>
          </div>
          <div className="summary-road-item">
            {isTour ? (
              <Fragment>
                <div className="text-sm">{t('search.destination')}</div>
                <div className="text-bold mb-10">
                  {cityFullDescription || details.deal.dealDestination}
                </div>
              </Fragment>
            ) : (
              <Fragment>
                <div>{t('search.check_out')}</div>
                {!!flight && (
                  <div className="text-bold mb-10">
                    {dateFormat(
                      `${flight?.inbound.date}T${flight?.inbound.depTime}`,
                      'EEE - dd MMMM, yyyy'
                    )}
                  </div>
                )}
                {!!flightDeal && (
                  <div className="text-bold mb-10">
                    {dateFormat(
                      `${flightDeal.inbound?.[0].date}T${flightDeal.inbound?.[0].depTime}`,
                      'EEE - dd MMMM, yyyy'
                    )}
                  </div>
                )}
              </Fragment>
            )}
            <div>{t('passenger.room_count')}</div>
            <div className="text-bold mb-10">{rooms.length}</div>
          </div>
        </div>
        <div
          className="text-contrast cur-poi text-underline mb-15 text-sm"
          onClick={() => setIsMoreDetailsOpen(true)}
        >
          {t('package.more_info')}
        </div>
        {!!props.netravelPage?.page?.languages?.[0]?.title_3?.body && (
          <div className="text-lh mb-20">
            <div className="divider mb-20"></div>
            <div className="text-contrast text-bold">
              {props.netravelPage.page.languages[0].title_3.desc}
            </div>
            <div
              className="text-st"
              dangerouslySetInnerHTML={{
                __html: props.netravelPage.page.languages[0].title_3.body,
              }}
            />
          </div>
        )}
        {!successPage && (
          <Fragment>
            <div className="divider mb-20"></div>
            <div
              className="title text-st text-contrast mb-20 text-underline"
              onClick={() => setIsTermsOpen(true)}
            >
              <FontAwesomeIcon icon={faFileLines} className="mr-5" size="sm" />
              {t('flight.info_and_conditions')}
            </div>
            <HandleFee name="LDS" />
          </Fragment>
        )}
        <div className="divider mb-20"></div>
        <div className="flex-between mb-15 title text-st text-contrast">
          <div className="">
            <FontAwesomeIcon icon={faMoneyBill1 as any} className="mr-5" size="sm" />
            {t('flight.booking_summary')}
          </div>
          <div>{formatPrice(totalPrice, currency)}</div>
        </div>
        {!successPage && (
          <div className="text-sm mb-20">
            {rooms.map((room, index) => (
              <Fragment key={index}>
                <div className="mb-10 flex-between" key={index}>
                  <div>
                    <div className="mb-10 text-bold">
                      <span className="ticket-badge">{index + 1}</span>
                      <span className="ml-5">{t('passenger.room')} </span>
                      <span className="text-light">({formatOccupancySrt(room.complect)})</span>
                    </div>

                    <div className="ml-28 text-sm">
                      <span className="text-cap">{room.roomType?.toLowerCase()} </span>
                      <span className="text-light">
                        {itineraries?.length && isTour
                          ? `(${itineraries[0].boardDesc})`
                          : getFare(room.fareBasis, fareBasisBoardDesc)}
                      </span>
                    </div>
                  </div>

                  <div className="text-bold flex-col-end">
                    {formatPrice(room.salePrice, room.currency)}
                  </div>
                </div>
                {props.additionalPaymentsSelected
                  .filter((p) => p.roomIndex === index)
                  .map((payment, index) => (
                    <div className="mb-10 flex-between" key={index}>
                      {(payment.displayType || payment.restType) === 'PT' && (
                        <div>
                          <div className="text-sm">{getPaymentDesc(payment)}</div>
                          <div className="text-light">{t('package.per_trip')}</div>
                        </div>
                      )}

                      {['OWDEP', 'PL', 'PL1'].includes(payment.displayType || payment.restType) &&
                        payment.legId === 1 && (
                          <div>
                            <div className="text-sm">{getPaymentDesc(payment)}</div>
                            <div className="text-light">{t('flight.outbound')}</div>
                          </div>
                        )}
                      {['OWDEP', 'PL', 'PL2'].includes(payment.displayType || payment.restType) &&
                        payment.legId === 2 && (
                          <div>
                            <div className="text-sm">{getPaymentDesc(payment)}</div>
                            <div className="text-light">{t('flight.inbound')}</div>
                          </div>
                        )}
                      <div className="text-bold">{formatPrice(payment.addPrice, currency)}</div>
                    </div>
                  ))}
              </Fragment>
            ))}
            {!!infantsCount && (
              <div className="mb-10 flex-between">
                <div className="mb-10 text-bold">
                  <span className="ticket-badge">{infantsCount}</span>
                  <span className="ml-5">{t('search.infants')} </span>
                </div>

                <div className="text-bold">{formatPrice(infantPrice * infantsCount, currency)}</div>
              </div>
            )}
          </div>
        )}
        <div className="divider" ref={totalRef}></div>
        <div
          className={`mt-20 flex-between text-lg text-bold ${
            isLockClass ? 'package-total-sticky' : ''
          }${window.innerWidth > 768 ? ' title-footer-grey' : ''}`}
          onClick={() => {
            window.innerWidth < 768 &&
              window.scrollTo({
                top: 0,
                behavior: 'smooth',
              });
          }}
        >
          <div>{t('flight.total_per_package')}</div>
          <div className="text-contrast display-flex">
            {formatPrice(totalPrice, currency)}
            <FontAwesomeIcon
              icon={faChevronUp}
              className="ml-10"
              size="sm"
              style={{ display: 'none' }}
            />
          </div>
        </div>
        {isTermsOpen && (
          <GeneralConditionsModal
            onClose={() => setIsTermsOpen(false)}
            product="LDS"
            package={props.packageDetailsParams}
          />
        )}
        {isMoreDetailsOpen && (
          <MoreDetailsModal dealModel={details} onClose={() => setIsMoreDetailsOpen(false)} />
        )}
      </div>

      {!successPage && (
        
          <SalesPrice
            currency={details.deal.currency}
            setSaleValue={props.setCouponAmount}
            saleValue={props.couponAmount}
            price={totalPrice}
            onClickApply={props.onClickApply}
          />
        
      )}
    </div>
  );
};

export default withErrorBoundary(BookSummary, 'BookSummary');
